import React, { useState } from 'react';
import TopPage from 'components/Shared/TopPage';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import DownloadIcon from '@mui/icons-material/Download';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ScaleIcon from '@mui/icons-material/Scale';
import DescriptionIcon from '@mui/icons-material/Description';
import { SvgIconComponent } from '@mui/icons-material';

import { useSnackbar } from 'notistack';
import { downloadReports } from 'services/antt';
import { primary, secondary } from 'helpers/colors';

type ReportOption = {
  value: string;
  label: string;
  Icon: SvgIconComponent;
  filters: string;
  dateRef: string;
  filename: string;
  reportType?: string;
};

const REPORT_OPTIONS: ReportOption[] = [
  {
    value: 'geral',
    label: 'Relatório Geral',
    Icon: AssessmentIcon,
    filters: '&columns[0][name]=datetime&columns[0][order]=desc',
    dateRef: 'occurrence',
    filename: 'relatorio-geral.xlsx',
    reportType: 'geral_de_multas'
  },
  {
    value: 'boletos',
    label: 'Boletos Disponíveis',
    Icon: ReceiptIcon,
    filters:
      '&columns[0][name]=datetime&columns[0][order]=desc&columns[1][name]=invoices.available&columns[1][filter]=true',
    dateRef: 'due_date',
    filename: 'relatorio-boletos-disponiveis.xlsx',
    reportType: 'boletos'
  },
  {
    value: 'piso_minimo_frete',
    label: 'Piso Mínimo de Frete',
    Icon: LocalShippingIcon,
    filters: '&columns[0][name]=datetime&columns[0][order]=desc',
    dateRef: 'occurrence',
    filename: 'relatorio-piso-minimo-de-frete.xlsx',
    reportType: 'piso_minimo_frete'
  },
  {
    value: 'excesso_de_peso',
    label: 'Excesso de Peso',
    Icon: ScaleIcon,
    filters: '&columns[0][name]=datetime&columns[0][order]=desc',
    dateRef: 'occurrence',
    filename: 'relatorio-excesso-de-peso.xlsx',
    reportType: 'excesso_de_peso'
  }
];

function Reports() {
  const [selectedReport, setSelectedReport] = useState('geral');
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const selected = REPORT_OPTIONS.find(o => o.value === selectedReport) ?? REPORT_OPTIONS[0];

  const handleExport = () => {
    setLoading(true);
    const onSuccess = () => {
      setLoading(false);
      enqueueSnackbar('Relatório exportado com sucesso', { variant: 'success', autoHideDuration: 2000 });
    };
    const onError = () => {
      setLoading(false);
      enqueueSnackbar('Erro ao exportar relatório', { variant: 'error', autoHideDuration: 2000 });
    };

    const today = new Date().toISOString().split('T')[0];
    const baseName = selected.filename.replace('.xlsx', '');
    const filename = `${baseName}_${today}.xlsx`;

    downloadReports(onSuccess, { reportType: selected.reportType ?? '', filename }, onError);
  };

  return (
    <>
      <TopPage title='Relatórios' />

      <Card>
        <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <DescriptionIcon sx={{ color: '#9e9e9e', fontSize: 20, opacity: 0.6 }} />
            <Typography variant='subtitle1' fontWeight={600} color='text.secondary'>
              Selecione o relatório:
            </Typography>
          </Box>

          <RadioGroup value={selectedReport} onChange={e => setSelectedReport(e.target.value)}>
            <Grid container spacing={2} sx={{ mb: 4 }} alignItems='stretch'>
              {REPORT_OPTIONS.map(option => {
                const isSelected = selectedReport === option.value;
                return (
                  <Grid item xs={12} sm={6} key={option.value} sx={{ display: 'flex' }}>
                    <Box
                      onClick={() => setSelectedReport(option.value)}
                      sx={{
                        border: '1px solid',
                        borderColor: isSelected ? primary.main : '#e0e0e0',
                        borderRadius: 3,
                        pl: 2,
                        pr: { xs: 2, sm: 6 },
                        py: 2,
                        cursor: 'pointer',
                        backgroundColor: isSelected ? primary.lighter : '#fff',
                        transition: 'all 0.2s',
                        position: 'relative',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        '&:hover': {
                          borderColor: primary.main,
                          backgroundColor: primary.lighter
                        }
                      }}
                    >
                      <Radio value={option.value} sx={{ p: 0, mr: 1.5, flexShrink: 0 }} />
                      <Typography variant='body2' fontWeight={500} sx={{ lineHeight: 1.3 }}>
                        {option.label}
                      </Typography>
                      <option.Icon
                        sx={{
                          display: { xs: 'none', sm: 'block' },
                          position: 'absolute',
                          right: 12,
                          fontSize: 32,
                          color: isSelected ? primary.main : '#e0e0e0',
                          transition: 'all 0.2s',
                          pointerEvents: 'none'
                        }}
                      />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </RadioGroup>

          <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
            <Button
              variant='contained'
              fullWidth={false}
              startIcon={loading ? <CircularProgress size={16} color='inherit' /> : <DownloadIcon />}
              disabled={loading}
              onClick={handleExport}
              disableElevation
              sx={{
                backgroundColor: secondary.main,
                borderRadius: 50,
                px: 4,
                color: secondary.contrast,
                width: { xs: '100%', sm: 'auto' },
                '&:hover': { backgroundColor: secondary.hover }
              }}
            >
              Exportar
            </Button>
          </Box>
        </CardContent>
      </Card>
    </>
  );
}

export default Reports;
